import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { useOnboarding } from "@/hooks/useOnboarding";
import { capitalize, classnames, isSessionExpired } from "@/utils";
import BrandLogo from "@/components/BrandLogo";
import Container from "@/components/Container";
import AppIcon from "@/assets/app-icon.svg?react";
import Platform from "@/components/Platform";
import useDevice from "@/hooks/useDevice";
import Mixpanel from "@/services/tracking";
import styles from "./Pages.module.scss";
import fallbackQrCodeSrc from "@/assets/onelink-qr-code.png";
import { STORAGE_KEY } from "@/constants";

const fallbackDownloadLink = "https://accumbo.page.link/get-started";

export default function AppDownload() {
  const { saveAnswers } = useApi();
  const { session } = useAuth();
  const { answers, height, weight, currentOnboardingFlow } = useOnboarding();
  const { isAppleDevice, isMobileOrTablet } = useDevice();

  const downloadLinkUrl =
    // @ts-ignore
    window.AF_SMART_SCRIPT_RESULT?.clickURL || fallbackDownloadLink;

  useEffect(() => {
    saveAnswers(answers, currentOnboardingFlow, height, weight)
      .then(() => {
        Mixpanel.track("OnboardingCompleted", { flow: currentOnboardingFlow });
        Mixpanel.register({ flow: null });
        // Clear stored onboarding state to allow the same device to complete both hypertension and obesity onboarding
        localStorage.removeItem(STORAGE_KEY);
      })
      .catch((e) => console.log("error", e));
  }, [answers, height, weight, saveAnswers]);

  useEffect(() => {
    if (isMobileOrTablet) {
      return;
    }
    // @ts-ignore
    if (window.AF_SMART_SCRIPT) {
      try {
        // @ts-ignore
        window.AF_SMART_SCRIPT.displayQrCode("appsflyer-qr-code");
      } catch (e) {
        console.log(e);
      }
    }
  }, [isMobileOrTablet]);

  if (!session?.jwt || isSessionExpired(session)) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container>
      <BrandLogo centered />
      <Platform desktop>
        <div className="flex justify-center mt-20 mb-20">
          {/* @ts-ignore */}
          {window.AF_SMART_SCRIPT ? (
            <div className={styles.qrWrapper} id="appsflyer-qr-code"></div>
          ) : (
            <div className={styles.qrWrapper}>
              <img src={fallbackQrCodeSrc} />
            </div>
          )}
        </div>
      </Platform>
      <Platform mobile>
        <div className={styles.appIconWrapper}>
          <a href={downloadLinkUrl} target="_blank">
            <AppIcon />
          </a>
        </div>
      </Platform>
      <h1 className="text-center">
        Vi fortsätter i appen,{" "}
        {capitalize(session.user?.givenName?.toLowerCase())}!
      </h1>
      <Platform desktop>
        <p className="text-center fw-500">
          Skanna QR-koden ovan med din mobil för att ladda ner appen och komma
          igång med din behandling.
        </p>
        <ul className="fw-500">
          <li>Enkel inloggning med BankID</li>
          <li>Du förbinder dig inte till något</li>
          <li>Avsluta när du vill</li>
        </ul>
      </Platform>
      <Platform mobile>
        <p className="text-center">
          Ladda ner appen för att prata med oss och komma ett steg närmare
          starten på din behandling.
        </p>
        <div className="text-center mt-40">
          <a
            href={downloadLinkUrl}
            className={classnames([
              styles.storeLink,
              isAppleDevice ? styles.apple : styles.google,
            ])}
          >
            Appnedladdning
          </a>
        </div>
      </Platform>
    </Container>
  );
}
